import React from "react";
import DSGrid from "../../system-design/DSGrid";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSSkeleton from "../../system-design/DSSkeleton";
import DSAlert from "../../system-design/DSAlert";
import { dateToDateTime } from "../../../utils/util";
import CalendarEventDay from "./CalendarEventDay";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";

type CalendarGridProps = {
  currentDate: Date;
  events: PopulatedCalendarEvent[];
  country: string;
  isLoading: boolean;
  error: Error | null;
  hasCountry: boolean;
};

const CalendarGrid = ({
  currentDate,
  events,
  country,
  isLoading,
  error,
  hasCountry,
}: CalendarGridProps) => {
  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: Date[] = [];

    const startDay = firstDay.getDay();
    for (let i = 0; i < startDay; i++) {
      const emptyDay = new Date(year, month, -(startDay - i - 1));
      days.push(emptyDay);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    while (days.length < 42) {
      const nextDay = new Date(
        year,
        month + 1,
        days.length - lastDay.getDate() - startDay + 1,
      );
      days.push(nextDay);
    }

    return days;
  };

  const getCalendarEventsForDate = (date: Date): PopulatedCalendarEvent[] => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    return events.filter((event) => {
      const calendarEventDateTime = event.date;
      const calendarEventDateStr = `${calendarEventDateTime.year}-${String(calendarEventDateTime.month).padStart(2, "0")}-${String(calendarEventDateTime.day).padStart(2, "0")}`;
      return calendarEventDateStr === dateStr;
    });
  };

  const isCurrentMonth = (date: Date): boolean => {
    return (
      date.getMonth() === currentDate.getMonth() &&
      date.getFullYear() === currentDate.getFullYear()
    );
  };

  if (error) {
    return (
      <DSBox sx={{ p: 2 }}>
        <DSAlert severity="error">
          Failed to load event data: {error.message}
        </DSAlert>
      </DSBox>
    );
  }

  if (isLoading) {
    return (
      <DSGrid container spacing={1}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <DSGrid item xs={12 / 7} key={day}>
            <DSBox sx={{ p: 1, textAlign: "center" }}>
              <DSTypography variant="subtitle2" color="textSecondary">
                {day}
              </DSTypography>
            </DSBox>
          </DSGrid>
        ))}

        {Array.from({ length: 42 }).map((_, index) => (
          <DSGrid item xs={12 / 7} key={index}>
            <DSSkeleton
              variant="rectangular"
              height={120}
              animation="wave"
              sx={{ borderRadius: 1 }}
            />
          </DSGrid>
        ))}
      </DSGrid>
    );
  }

  const days = getDaysInMonth(currentDate);

  return (
    <DSBox>
      <DSGrid container spacing={1}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <DSGrid item xs={12 / 7} key={day}>
            <DSBox sx={{ p: 1, textAlign: "center" }}>
              <DSTypography variant="subtitle2" color="textSecondary">
                {day}
              </DSTypography>
            </DSBox>
          </DSGrid>
        ))}

        {days.map((date, index) => (
          <DSGrid item xs={12 / 7} key={index}>
            <CalendarEventDay
              date={dateToDateTime(date)}
              event={getCalendarEventsForDate(date)}
              isCurrentMonth={isCurrentMonth(date)}
              isToday={date.toDateString() === new Date().toDateString()}
              country={country}
              hasCountry={hasCountry}
            />
          </DSGrid>
        ))}
      </DSGrid>
    </DSBox>
  );
};

export default CalendarGrid;
